/**
 * WebGL-based 3D Gaussian smoothing for SDF volumes.
 *
 * This module provides GPU-accelerated Gaussian smoothing using separable
 * convolution (X, Y, Z passes separately) for optimal performance.
 */

import { vec2 } from 'gl-matrix';
import { gl } from '../../../Engine';
import { Renderer } from '../../../core/render/Renderer';
import { RenderStorage } from '../../../core/render/RenderStorage';
import { FrameBufferObject } from '../../../core/utils/FrameBufferObject';
import { ShaderProgram } from '../../../core/utils/ShaderProgram';
import { UniformFloat } from '../../../core/utils/uniforms/UniformFloat';
import { UniformInt } from '../../../core/utils/uniforms/UniformInt';
import { ContourTexture } from '../../../data/structure/ContourTexture';
import { getSigmaForLevel } from './GaussianSmoothing';
import {
    copyTexture3D,
    createSdfTexture3D,
    readR8Texture3D,
    renderSlicePass,
} from './Texture3DUtils';
import { SDFOperations } from './SDFOperations';
import { GlOperationGuard, maybeYield, resetGlDefaults } from './utils';

/**
 * Shader for separable 1D Gaussian blur along a specified axis.
 * Uses a full-screen quad approach with 3D texture sampling.
 */
class GaussianBlurShader extends ShaderProgram {
    private static readonly VERTEX_CONTENT = `#version 300 es
        precision highp float;

        in vec2 position;

        out vec2 vUV;

        void main() {
            // Position is in [-1, 1], convert to [0, 1] for UV
            vUV = position * 0.5 + 0.5;
            gl_Position = vec4(position, 0.0, 1.0);
        }`;

    private static readonly FRAGMENT_CONTENT = `#version 300 es
        precision highp float;
        precision highp sampler3D;

        in vec2 vUV;
        out vec4 fragColor;

        uniform sampler3D uSdfTexture;
        uniform int uAxis;           // 0 = X, 1 = Y, 2 = Z
        uniform float uSliceZ;       // Normalized Z coordinate of current slice
        uniform int uKernelRadius;   // Kernel half-width
        uniform float uKernel[32];   // Pre-computed kernel weights (max radius 15)
        uniform vec3 uTexelSize;     // 1.0 / texture dimensions

        void main() {
            vec3 texCoord = vec3(vUV, uSliceZ);
            float result = 0.0;

            // Determine step direction based on axis
            vec3 step = vec3(0.0);
            if (uAxis == 0) {
                step.x = uTexelSize.x;
            } else if (uAxis == 1) {
                step.y = uTexelSize.y;
            } else {
                step.z = uTexelSize.z;
            }

            // Apply 1D convolution along the specified axis
            for (int k = -15; k <= 15; k++) {
                if (k < -uKernelRadius || k > uKernelRadius) continue;

                vec3 sampleCoord = texCoord + step * float(k);
                // Clamp to valid texture coordinates
                sampleCoord = clamp(sampleCoord, vec3(0.0), vec3(1.0));

                float weight = uKernel[k + uKernelRadius];
                result += texture(uSdfTexture, sampleCoord).r * weight;
            }

            fragColor = vec4(result, result, result, 1.0);
        }`;

    public readonly uAxis: UniformInt;
    public readonly uSliceZ: UniformFloat;
    public readonly uKernelRadius: UniformInt;
    public readonly uTexelSizeX: UniformFloat;
    public readonly uTexelSizeY: UniformFloat;
    public readonly uTexelSizeZ: UniformFloat;

    private kernelLocation: WebGLUniformLocation | null = null;
    private texelSizeLocation: WebGLUniformLocation | null = null;

    public constructor() {
        super(
            GaussianBlurShader.VERTEX_CONTENT,
            GaussianBlurShader.FRAGMENT_CONTENT,
            'position'
        );
        this.uAxis = new UniformInt('uAxis');
        this.uSliceZ = new UniformFloat('uSliceZ');
        this.uKernelRadius = new UniformInt('uKernelRadius');
        this.uTexelSizeX = new UniformFloat('uTexelSizeX');
        this.uTexelSizeY = new UniformFloat('uTexelSizeY');
        this.uTexelSizeZ = new UniformFloat('uTexelSizeZ');

        super.storeUniformLocations(
            this.uAxis,
            this.uSliceZ,
            this.uKernelRadius
        );

        // Store kernel array location manually
        this.kernelLocation = gl.getUniformLocation(
            this.programID,
            'uKernel[0]'
        );
        this.texelSizeLocation = gl.getUniformLocation(
            this.programID,
            'uTexelSize'
        );

        // Set sampler uniform
        const samplerLocation = gl.getUniformLocation(
            this.programID,
            'uSdfTexture'
        );
        this.start();
        gl.uniform1i(samplerLocation, 0); // Use texture unit 0
        this.stop();
    }

    public loadKernel(kernel: Float32Array): void {
        if (this.kernelLocation) {
            gl.uniform1fv(this.kernelLocation, kernel);
        }
    }

    public loadTexelSize(x: number, y: number, z: number): void {
        if (this.texelSizeLocation) {
            gl.uniform3f(this.texelSizeLocation, x, y, z);
        }
    }
}

/**
 * WebGL renderer for Gaussian smoothing operations on SDF textures.
 */
export class GaussianSmoothingRenderer implements Renderer {
    private shader: GaussianBlurShader;

    public constructor() {
        this.shader = new GaussianBlurShader();
    }

    /**
     * Apply Gaussian smoothing to a ContourTexture using WebGL.
     *
     * @param src Source ContourTexture containing the SDF to smooth
     * @param level Smoothing level (1-5)
     * @param iterations Number of smoothing iterations
     * @param signal Optional AbortSignal for cancellation
     * @returns New ContourTexture with smoothed SDF, or undefined if failed
     */
    public async applySmoothing(
        src: ContourTexture,
        level: number,
        iterations: number = 1,
        signal?: AbortSignal
    ): Promise<ContourTexture | undefined> {
        if (!src.signedDistanceField) {
            return undefined;
        }

        const sdf = src.signedDistanceField;
        const nx = sdf.size[0];
        const ny = sdf.size[1];
        const nz = sdf.size[2];

        if (nx === 0 || ny === 0 || nz === 0) {
            return undefined;
        }

        const sigma = getSigmaForLevel(level);
        let lastYield = performance.now();

        // Compute per-axis sigma in voxel units so that the physical
        // smoothing extent (in mm) is the same along every axis.
        // The base sigma is interpreted relative to the XY voxel spacing;
        // the Z sigma is scaled down when Z spacing is coarser.
        const grid = src.textureGrid;
        const sigmaMm = sigma * grid.resX;
        const sigmaX = sigmaMm / grid.resX; // = sigma (unchanged)
        const sigmaY = sigmaMm / grid.resY;
        const sigmaZ = sigmaMm / grid.resZ;

        // Build per-axis 1D Gaussian kernels.
        // Radius is clamped to 15 to match the shader's uKernel[32] array
        // (max size = 2*15+1 = 31) and the hardcoded loop bound of ±15.
        const MAX_KERNEL_RADIUS = 15;
        const kernels = [sigmaX, sigmaY, sigmaZ].map((s) => {
            const r = Math.min(
                MAX_KERNEL_RADIUS,
                Math.max(1, Math.ceil(3 * s))
            );
            const size = 2 * r + 1;
            const k = new Float32Array(32);
            let sum = 0;
            for (let i = -r; i <= r; i++) {
                const v = Math.exp(-(i * i) / (2 * s * s));
                k[i + r] = v;
                sum += v;
            }
            for (let i = 0; i < size; i++) {
                k[i] /= sum;
            }
            return { kernel: k, rad: r };
        });

        // Create two temporary 3D textures for ping-pong rendering
        const tempTex1 = createSdfTexture3D(nx, ny, nz);
        const tempTex2 = createSdfTexture3D(nx, ny, nz);

        if (!tempTex1 || !tempTex2) {
            console.error('Failed to create temporary textures');
            return undefined;
        }

        // Copy source SDF to tempTex1
        copyTexture3D(sdf.texture, tempTex1, nx, ny, nz);

        const texelSize = [1.0 / nx, 1.0 / ny, 1.0 / nz];

        // Create FBO for rendering
        const fbo = new FrameBufferObject(vec2.fromValues(nx, ny));

        let srcTex = tempTex1;
        let dstTex = tempTex2;

        for (let it = 0; it < iterations; it++) {
            for (let axis = 0; axis < 3; axis++) {
                this.shader.start();
                this.shader.loadTexelSize(
                    texelSize[0],
                    texelSize[1],
                    texelSize[2]
                );
                gl.viewport(0, 0, nx, ny);
                RenderStorage.getQuadVAO().bind(0);

                this.shader.uAxis.loadInt(axis);
                this.shader.uKernelRadius.loadInt(kernels[axis].rad);
                this.shader.loadKernel(kernels[axis].kernel);

                renderSlicePass(
                    srcTex,
                    dstTex,
                    fbo,
                    this.shader.uSliceZ,
                    nz,
                    signal
                );
                [srcTex, dstTex] = [dstTex, srcTex];

                RenderStorage.getQuadVAO().unbind(0);
                this.shader.stop();
                fbo.unbind();
                resetGlDefaults();

                lastYield = await maybeYield(lastYield, signal);
            }
        }

        fbo.delete();

        // Read result back from srcTex (last destination)
        const resultPixels = readR8Texture3D(srcTex, nx, ny, nz, signal);

        // Clean up temporary textures
        gl.deleteTexture(tempTex1);
        gl.deleteTexture(tempTex2);

        resetGlDefaults();

        // Create result ContourTexture
        return src.cloneWithNewSdf(resultPixels);
    }

    public delete(): void {
        this.shader.delete();
    }
}

/**
 * Apply WebGL-accelerated Gaussian smoothing to a ContourTexture.
 *
 * The source SDF is first reinitialized via
 * {@link SDFOperations.reinitializeSDFWebGL} to ensure it is a proper
 * metric distance field (especially along the Z axis for structures
 * with contours on only a few slices). This prevents aggressive
 * smoothing artifacts from non-metric distance values.
 *
 * @param src Source ContourTexture
 * @param level Smoothing level (1-5)
 * @param iterations Number of smoothing iterations (default: 1)
 * @param signal Optional AbortSignal for cancellation
 * @returns New smoothed ContourTexture, or undefined if failed
 */
export async function applyGaussianSmoothingGL(
    src: ContourTexture,
    level: number,
    iterations: number = 1,
    signal?: AbortSignal
): Promise<ContourTexture | undefined> {
    if (!GlOperationGuard.acquire('Gaussian smoothing')) {
        return undefined;
    }
    try {
        // Reinitialize SDF to a proper metric distance field before
        // smoothing. This propagates distance values in all directions
        // (especially Z) and prevents aggressive smoothing on
        // structures with contours on only a few slices.
        const reinitTexture = await SDFOperations.reinitializeSDFWebGL(src);
        if (!reinitTexture) {
            return undefined;
        }

        const renderer = RenderStorage.getRenderer(GaussianSmoothingRenderer);
        const result = await renderer.applySmoothing(
            reinitTexture,
            level,
            iterations,
            signal
        );
        reinitTexture.delete();
        return result;
    } finally {
        GlOperationGuard.release();
    }
}
